<template>
  <div
    v-if="isLoggedIn"
    class="account-menu"
    style="position: relative"
    v-click-outside="closeMenu"
  >
    <span class="profile-pic" @click="showMenu = !showMenu" style="cursor: pointer">
      <img
        v-if="user?.avatar || user?.logo"
        :src="user?.avatar || user?.logo"
        alt="Profile"
        class="avatar"
      />
      <span v-else class="avatar-placeholder">
        {{ (user?.name?.[0] || "") + (user?.surname?.[0] || "") }}
      </span>
    </span>
    <span @click="showMenu = !showMenu" style="cursor: pointer">
      {{ user?.name + " " + user?.surname }}
    </span>
    <div v-if="showMenu" class="submenu">
      <router-link :to="{ name: 'AccountSettings' }" class="submenu-item">{{
        t("My account")
      }}</router-link>
      <div @click="handleLogout" class="submenu-item logout">
        {{ t("Sign Off") }}
      </div>
    </div>
  </div>

  <div v-else class="guest-account" v-click-outside="closeGuestPopup">
    <button class="account-icon-btn" @click="showGuestPopup = !showGuestPopup" :aria-label="t('login')">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="8" r="4"/>
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
      </svg>
    </button>

    <Transition name="popup">
      <div v-if="showGuestPopup" class="auth-popup">
        <p class="auth-popup__subtitle">{{ t("loginOrRegister") }}</p>
        <router-link :to="{ name: 'Login' }" class="auth-popup__btn auth-popup__btn--primary" @click="closeGuestPopup">
          {{ t("login") }}
        </router-link>
        <router-link :to="{ name: 'Register' }" class="auth-popup__btn auth-popup__btn--outline" @click="closeGuestPopup">
          {{ t("register") }}
        </router-link>
      </div>
    </Transition>
  </div>
</template>

<script>
import CustomerHelper from "@/helpers/customerHelper";
import { computed, defineComponent, onMounted, ref } from "vue";
import { useAuthStore } from "@/stores/useAuthStore";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "AccountMenu",
  setup() {
    const { t } = useI18n();
    const authStore = useAuthStore();

    const user = computed(() => authStore.user ?? CustomerHelper.getUser());
    const isLoggedIn = computed(() => !!user.value);
    const showGuestPopup = ref(false);

    onMounted(() => {
      authStore.loadUserFromStorage();
    });

    const logout = () => {
      CustomerHelper.logout({ redirect: false });
    };

    return {
      t,
      user,
      isLoggedIn,
      logout,
      showGuestPopup,
    };
  },
  data() {
    return {
      showMenu: false,
    };
  },
  methods: {
    handleLogout() {
      this.logout();
      this.showMenu = false;
      this.$router.push({ name: "Login" });
    },
    closeMenu() {
      this.showMenu = false;
    },
    closeGuestPopup() {
      this.showGuestPopup = false;
    },
  },
});
</script>

<style scoped>
/* ── Logged-in menu ── */
.submenu {
  position: absolute;
  top: 48px;
  right: 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-width: 150px;
  z-index: 10;
}

.submenu-item {
  display: block;
  padding: 10px 16px;
  color: #333;
  text-decoration: none;
  text-align: left;
}

.submenu-item.logout {
  display: block;
  padding: 10px 16px;
  color: #e53935;
  cursor: pointer;
}

/* ── Avatar ── */
.profile-pic {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e0e0e0;
  background: #fff;
}

.avatar-placeholder {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #447bfe;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.1rem;
  border: 2px solid #e0e0e0;
  text-transform: uppercase;
}

/* ── Guest account icon + popup ── */
.guest-account {
  position: relative;
}

.account-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 50%;
  padding: 0;
  cursor: pointer;
  color: inherit;
  transition: background 200ms;
}

.account-icon-btn:hover {
  background: rgba(0, 0, 0, 0.07);
}

.auth-popup {
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  background: #fff;
  border-radius: 1.4rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.13);
  padding: 2rem;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  z-index: 100;
}

.auth-popup__subtitle {
  font-size: 1.3rem;
  color: #627282;
  margin-bottom: 0.4rem;
}

.auth-popup__btn {
  display: block;
  text-align: center;
  padding: 0.85rem 1.2rem;
  border-radius: 0.8rem;
  font-size: 1.4rem;
  font-weight: 600;
  transition: opacity 200ms;
}

.auth-popup__btn--primary {
  background: #0f1f2e;
  color: #fff;
}

.auth-popup__btn--primary:hover {
  opacity: 0.88;
}

.auth-popup__btn--outline {
  border: 1.5px solid #0f1f2e;
  color: #0f1f2e;
}

.auth-popup__btn--outline:hover {
  background: #f5f7f9;
}

/* ── Transition ── */
.popup-enter-active,
.popup-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.popup-enter-from,
.popup-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
